import { forwardRef, useImperativeHandle } from 'react';
import { useInstantSearch, usePagination } from 'react-instantsearch';

import { hitsPerPage as searchForumThreadHitsPerPage } from '../config';

export interface NavigateHandleRef {
  navigateToNextCommentPage: () => void;
}

/**
 * Exposes imperative navigation into the InstantSearch pagination state.
 * Must be rendered inside a SearchForumThread (InstantSearch) context.
 */
const NavigateHandle = forwardRef<NavigateHandleRef>((props, ref) => {
  const { results } = useInstantSearch();
  const { refine: setCurrentPage } = usePagination();

  useImperativeHandle(ref, () => ({
    /**
     * Navigates to the page where the next new comment will appear.
     * Call this after successfully adding a comment, using the current
     * nbHits to calculate which page slot the new (nbHits + 1)th comment
     * will occupy.
     */
    navigateToNextCommentPage: () => {
      const nbHits = results?.nbHits ?? 0;
      const hitsPerPage = results?.hitsPerPage ?? searchForumThreadHitsPerPage;

      // The new comment will be hit number (nbHits + 1).
      // usePagination.refine uses 0-indexed pages.
      const nextPage = Math.ceil((nbHits + 1) / hitsPerPage) - 1;
      setCurrentPage(nextPage);
    },
  }), [results, setCurrentPage]);

  return null;
});

NavigateHandle.displayName = 'NavigateHandle';

export default NavigateHandle;
